import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ActionIcon, Box, Group, Table, Text as MantineText } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconPlus } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import {
  Button,
  Container,
  EmptyMessage,
  LayoutStandard,
  Loader,
  Modal,
} from "components";
import { AuthorityConstants } from "constant";
import { MotivationalMessageService } from "services";
import { tableClasses } from "styles";
import { MotivationalMessageResponse } from "types";
import { AppUtils, UserUtils } from "utils";

import MotivationalMessageFormModal from "./MotivationalMessageFormModal";
import classes from "./MotivationalMessagesPage.module.css";

const resolveMessageDate = (message: MotivationalMessageResponse) =>
  message.info.modifiedAt ?? message.info.createdAt;

const sortMessages = (items: MotivationalMessageResponse[]) =>
  [...items].sort((left, right) => {
    if (left.current !== right.current) {
      return left.current ? -1 : 1;
    }

    return new Date(resolveMessageDate(right)).getTime() - new Date(resolveMessageDate(left)).getTime();
  });

const MotivationalMessagesPage = () => {
  const { t, i18n } = useTranslation();
  const [messages, setMessages] = useState<MotivationalMessageResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [formOpened, setFormOpened] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState<number | undefined>(undefined);
  const [messageToActivate, setMessageToActivate] = useState<MotivationalMessageResponse | null>(null);
  const [messageToDelete, setMessageToDelete] = useState<MotivationalMessageResponse | null>(null);
  const [isActivating, setIsActivating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const tRef = useRef(t);

  const canViewMotivationalMessages = UserUtils.hasAnyAuthority([
    AuthorityConstants.ROLE_MM_R,
    AuthorityConstants.ROLE_MM_CRUD,
  ]);
  const canManageMotivationalMessages = UserUtils.hasAuthority(AuthorityConstants.ROLE_MM_CRUD);

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const loadMessages = useCallback(async () => {
    setIsLoading(true);

    try {
      const data = await MotivationalMessageService.getMotivationalMessages();
      setMessages(sortMessages(data));
    } catch {
      notifications.show({
        color: "red",
        message: tRef.current("motivationalMessages.notifications.loadErrorMessage"),
        title: tRef.current("motivationalMessages.notifications.loadErrorTitle"),
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canViewMotivationalMessages) {
      setIsLoading(false);
      return;
    }

    void loadMessages();
  }, [canViewMotivationalMessages, loadMessages]);

  const closeFormModal = useCallback(() => {
    setFormOpened(false);
    setEditingMessageId(undefined);
  }, []);

  const openCreateModal = useCallback(() => {
    if (!canManageMotivationalMessages) {
      return;
    }

    setEditingMessageId(undefined);
    setFormOpened(true);
  }, [canManageMotivationalMessages]);

  const openEditModal = useCallback((messageId: number) => {
    if (!canManageMotivationalMessages) {
      return;
    }

    setEditingMessageId(messageId);
    setFormOpened(true);
  }, [canManageMotivationalMessages]);

  const getPreviewText = useCallback(
    (message: MotivationalMessageResponse) => {
      const preferredLanguage = i18n.language.split("-")[0].toUpperCase();
      const preferredOrder = [preferredLanguage, "SR", "DE", "EN"];

      for (const language of preferredOrder) {
        const translation = message.translations.find((item) => item.language === language);
        if (translation?.text.trim()) {
          return translation.text.trim();
        }
      }

      return message.translations.find((item) => item.text.trim())?.text.trim() ?? "";
    },
    [i18n.language],
  );

  const rows = useMemo(() => {
    return messages.map((message) => (
      <Table.Tr key={message.id}>
        <Table.Td
          className={classes.messageCell}
          data-label={t("motivationalMessages.table.message")}
        >
          <MantineText className={classes.messagePreview}>
            {getPreviewText(message)}
          </MantineText>
        </Table.Td>
        <Table.Td data-label={t("motivationalMessages.table.status")}>
          <MantineText className={message.current ? classes.statusCurrent : classes.statusInactive}>
            {message.current
              ? t("motivationalMessages.status.current")
              : t("motivationalMessages.status.inactive")}
          </MantineText>
        </Table.Td>
        <Table.Td data-label={t("motivationalMessages.table.modifiedAt")}>
          <div className={classes.dateTime}>
            <span>{t("format.time", { date: new Date(resolveMessageDate(message)) })}</span>
            <span>{t("format.date", { date: new Date(resolveMessageDate(message)) })}</span>
          </div>
        </Table.Td>
        {canManageMotivationalMessages && (
          <Table.Td
            className={classes.actionsCell}
            data-label={t("actions")}
          >
            <Group gap="xs" className={classes.actionGroup}>
              <Button
                className={classes.actionButton}
                px={0}
                size="xs"
                variant="subtle"
                onClick={() => openEditModal(message.id)}
              >
                {t("motivationalMessages.actions.edit")}
              </Button>
              <Button
                className={classes.actionButton}
                size="xs"
                px={0}
                variant="subtle"
                disabled={message.current}
                onClick={() => setMessageToActivate(message)}
              >
                {t("motivationalMessages.actions.activate")}
              </Button>
              <Button
                className={classes.actionButton}
                color="red"
                px={0}
                size="xs"
                variant="subtle"
                onClick={() => setMessageToDelete(message)}
              >
                {t("action.delete")}
              </Button>
            </Group>
          </Table.Td>
        )}
      </Table.Tr>
    ));
  }, [canManageMotivationalMessages, getPreviewText, messages, openEditModal, t]);

  const handleActivate = useCallback(async () => {
    if (!canManageMotivationalMessages || !messageToActivate) {
      return;
    }

    setIsActivating(true);

    try {
      await MotivationalMessageService.activateMotivationalMessage(messageToActivate.id);
      notifications.show({
        color: "green",
        message: tRef.current("motivationalMessages.notifications.activateSuccessMessage"),
        title: tRef.current("motivationalMessages.notifications.activateSuccessTitle"),
      });
      setMessageToActivate(null);
      await loadMessages();
    } catch {
      notifications.show({
        color: "red",
        message: tRef.current("motivationalMessages.notifications.activateErrorMessage"),
        title: tRef.current("motivationalMessages.notifications.activateErrorTitle"),
      });
    } finally {
      setIsActivating(false);
    }
  }, [canManageMotivationalMessages, loadMessages, messageToActivate]);

  const handleDelete = useCallback(async () => {
    if (!canManageMotivationalMessages || !messageToDelete) {
      return;
    }

    setIsDeleting(true);

    try {
      await MotivationalMessageService.deleteMotivationalMessage(messageToDelete.id);
      notifications.show({
        color: "green",
        message: tRef.current("motivationalMessages.notifications.deleteSuccessMessage"),
        title: tRef.current("motivationalMessages.notifications.deleteSuccessTitle"),
      });
      setMessageToDelete(null);
      await loadMessages();
    } catch {
      notifications.show({
        color: "red",
        message: tRef.current("motivationalMessages.notifications.deleteErrorMessage"),
        title: tRef.current("motivationalMessages.notifications.deleteErrorTitle"),
      });
    } finally {
      setIsDeleting(false);
    }
  }, [canManageMotivationalMessages, loadMessages, messageToDelete]);

  return (
    <LayoutStandard
      title={AppUtils.composeTabTitle(t("motivationalMessages.title"))}
      authority={[AuthorityConstants.ROLE_MM_R, AuthorityConstants.ROLE_MM_CRUD]}
    >
      <Container>
        <div className={classes.page}>
          <div className={classes.content}>
            <Box pos="relative">
              <Loader overlay={true} visible={isLoading} />

              <div className={classes.header}>
                <div className={classes.headerContent}>
                  <h2 className={classes.title}>{t("motivationalMessages.title")}</h2>
                  <div className={classes.description}>
                    {t("motivationalMessages.description")}
                  </div>
                </div>

                {canManageMotivationalMessages && (
                  <ActionIcon
                    aria-label={t("motivationalMessages.actions.create")}
                    color="green"
                    onClick={openCreateModal}
                    size="lg"
                    title={t("motivationalMessages.actions.create")}
                  >
                    <IconPlus size={18} />
                  </ActionIcon>
                )}
              </div>

              {messages.length === 0 && !isLoading ? (
                <EmptyMessage message={t("motivationalMessages.empty")} />
              ) : (
                <Table className={`${tableClasses.table} ${classes.table}`}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{t("motivationalMessages.table.message")}</Table.Th>
                      <Table.Th>{t("motivationalMessages.table.status")}</Table.Th>
                      <Table.Th>{t("motivationalMessages.table.modifiedAt")}</Table.Th>
                      {canManageMotivationalMessages && (
                        <Table.Th>{t("actions")}</Table.Th>
                      )}
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>{rows}</Table.Tbody>
                </Table>
              )}
            </Box>
          </div>
        </div>

        {canManageMotivationalMessages && (
          <MotivationalMessageFormModal
            messageId={editingMessageId}
            onClose={closeFormModal}
            onSaved={loadMessages}
            opened={formOpened}
            t={t}
          />
        )}

        {canManageMotivationalMessages && (
          <Modal
            opened={Boolean(messageToActivate)}
            onClose={() => setMessageToActivate(null)}
            title={t("motivationalMessages.activateConfirm.title")}
          >
            <MantineText>{t("motivationalMessages.activateConfirm.description")}</MantineText>
            <Group className={classes.modalActions} justify="center">
              <Button color="gray" variant="default" onClick={() => setMessageToActivate(null)}>
                {t("action.cancel")}
              </Button>
              <Button disabled={isActivating} loading={isActivating} onClick={handleActivate}>
                {t("motivationalMessages.actions.activate")}
              </Button>
            </Group>
          </Modal>
        )}

        {canManageMotivationalMessages && (
          <Modal
            opened={Boolean(messageToDelete)}
            onClose={() => setMessageToDelete(null)}
            title={t("motivationalMessages.deleteConfirm.title")}
          >
            <MantineText>{t("motivationalMessages.deleteConfirm.description")}</MantineText>
            <Group className={classes.modalActions} justify="center">
              <Button color="gray" variant="default" onClick={() => setMessageToDelete(null)}>
                {t("action.cancel")}
              </Button>
              <Button color="red" disabled={isDeleting} loading={isDeleting} onClick={handleDelete}>
                {t("action.delete")}
              </Button>
            </Group>
          </Modal>
        )}
      </Container>
    </LayoutStandard>
  );
};

export default MotivationalMessagesPage;
